import { ReactNode, useState } from "react";
import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { sanitizeUrl } from "@helpers";
import { IFoodProfileConsumer } from "@models/FoodProfileConsumer";
import { PermissionLevelEnum } from "@models/PermissionSection";
import Widget from "@components/Widget/Widget";
import { FoodSectionEnum } from "@pages/Food/Food.constants";
import FoodAffinity from "@pages/Food/components/SectionConsumer/Affinity";
import FoodExperience from "@pages/Food/components/SectionConsumer/Experience";
import FoodHeatScore from "@pages/Food/components/SectionConsumer/HeatScore";
import FoodRespondentsData, { AnalyticsType } from "@pages/Food/components/SectionConsumer/RespondentsData";
import FoodTabs from "@pages/Food/components/Tabs/Tabs";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import FoodSectionTitle from "@pages/Food/refresh/components/FoodSectionTitle/FoodSectionTitle";
import FoodSectionMoreLink from "@pages/Food/refresh/components/FoodSectionMoreLink/FoodSectionMoreLink";
import "./FoodConsumerSection.scss";

interface Props {
    word: string;
    data: IFoodProfileConsumer;
}

interface SmallWidget {
    key: string;
    title: string;
    testId: string;
    titleTestId: string;
    content: ReactNode;
}

/**
 * Consumer section of the refreshed Food Profile: Affinity, Experience, Heat Score and the Love-or-like-it
 * respondents breakdown. The widgets are shared with the legacy page; only layout and spacing are restyled here.
 */
const FoodConsumerSection: FCX<Props> = ({ word, data: consumer }) => {
    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();
    const [selectedAnalyticsType, setSelectedAnalyticsType] = useState<AnalyticsType>(AnalyticsType.Affinity);

    if (!consumer.hasData) return null;

    const {
        data: { link, keywordName, loveLikeIt, knowIt, haveTriedIt, dataByDemoGroups, heatScore },
        permissionLevel,
    } = consumer;
    const hasAccess = permissionLevel !== PermissionLevelEnum.None;
    const consumerPreferences = products[AppId.ConsumerPreferences];
    // A missing product counts as no access, so the link is never an enabled control that goes nowhere.
    const hasConsumerPreferences = !!consumerPreferences && !consumerPreferences.isDisabled;
    const productUrl = consumerPreferences?.url ? sanitizeUrl(consumerPreferences.url) : null;

    const requestTrial = (featureName: string, hideNotification?: boolean) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };

    const smallWidgets: SmallWidget[] = [
        {
            key: "affinity",
            title: "AFFINITY",
            testId: "blockFoodConsumerAffinity",
            titleTestId: "textFoodConsumerAffinityTitle",
            content: <FoodAffinity value={loveLikeIt}/>,
        },
        {
            key: "experience",
            title: "EXPERIENCE",
            testId: "blockFoodConsumerExperience",
            titleTestId: "textFoodConsumerExperienceTitle",
            content: <FoodExperience knowItValue={knowIt} haveTriedItValue={haveTriedIt}/>,
        },
    ];

    return (
        <div
            className="Food__section"
            id={FoodSectionEnum.Consumer}
            data-testid="blockFoodSectionConsumer"
        >
            <div
                className={cn("FoodConsumerSection", !hasAccess && "is-blocked")}
                data-testid="blockFoodConsumer"
            >
                <FoodSectionTitle
                    className="FoodConsumerSection__title"
                    title={`Consumer: ${word}`}
                    testId="textFoodConsumerTitle"
                />
                <div className="FoodConsumerSection__card">
                    {/* Without access the values are only blurred (CSS), so hide them from screen readers too. */}
                    <div className="FoodConsumerSection__grid" aria-hidden={hasAccess ? undefined : true}>
                        <div className="FoodConsumerSection__col">
                            <div className="FoodConsumerSection__row">
                                {smallWidgets.map(({ key, title, testId, titleTestId, content }) => (
                                    <Widget
                                        key={key}
                                        testId={testId}
                                        className="FoodConsumerSection__small-widget"
                                        headerContent={(
                                            <div className="FoodConsumerSection__centered-title" data-testid={titleTestId}>
                                                {title}
                                            </div>
                                        )}
                                    >
                                        <div className="FoodConsumerSection__centered-content">{content}</div>
                                    </Widget>
                                ))}
                            </div>
                            <Widget
                                testId="blockFoodConsumerHeatScore"
                                className="FoodConsumerSection__heat-score"
                                headerContent={<div data-testid="textFoodConsumerHeatScoreTitle">HEAT SCORE</div>}
                            >
                                <div className="FoodConsumerSection__full-height">
                                    <FoodHeatScore keywordName={keywordName} data={heatScore}/>
                                </div>
                            </Widget>
                        </div>
                        <Widget
                            className="FoodConsumerSection__respondents"
                            headerContent={(
                                <FoodTabs
                                    data={[
                                        { id: AnalyticsType.Affinity, title: 'Affinity' },
                                        { id: AnalyticsType.Experience, title: 'Experience' },
                                    ]}
                                    activeTabId={selectedAnalyticsType}
                                    onItemClick={(id) => setSelectedAnalyticsType(id as AnalyticsType)}
                                    testId="blockFoodConsumerRespondentsHeaderTabs"
                                />
                            )}
                        >
                            <div className="FoodConsumerSection__full-height">
                                <FoodRespondentsData
                                    selectedAnalyticsType={selectedAnalyticsType}
                                    data={dataByDemoGroups}
                                />
                            </div>
                        </Widget>
                    </div>
                    {!hasAccess && (
                        <PermissionOverlay
                            onContactUsClick={() => requestTrial('flavor')}
                            testId="blockFoodConsumerPermissionOverlay"
                        >
                            <div data-testid="textFoodConsumerPermissionOverlayContentLine-0">
                                Upgrade your subscription to learn
                            </div>
                            <div data-testid="textFoodConsumerPermissionOverlayContentLine-1">
                                what consumers think of <b>{keywordName}</b>.
                            </div>
                        </PermissionOverlay>
                    )}
                </div>
                <FoodSectionMoreLink
                    url={link ? sanitizeUrl(link) : productUrl}
                    text="More Consumer Preferences"
                    altText="Upgrade your subscription to explore consumer preferences."
                    hasAccess={hasConsumerPreferences}
                    onContactUsClick={() => requestTrial('flavor', true)}
                    testId="linkFoodConsumerMoreConsumerPreferences"
                />
            </div>
        </div>
    );
};

export default FoodConsumerSection;
